import { FC, useState } from 'react';
import styles from '../view_transaction/styles.module.scss';
import ReportProblemButton from '@components/common/report_problem_button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import type { TCardTransactionWithReferenceCode } from 'src/types/reference_code';
import { currencies } from '@const/currency';
import { formatDate, getTransactionStatus, prettifyAmount } from '@utils/index';
import { ETransactionStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import { formatFeeRate, getFeesPlanSendAndRequest, getInternalTransferAction } from '@utils/payment';
import { EFundsOperation } from '@enum';
import { useUserContext } from '@hooks/context/useUserContext';
import Typography from '@components/common/typography';
import InfoBlock from '@components/common/info_block';
import ReportProblemSubview from '../report_problem_subview';

const ViewCardTransactionModal: FC<IModalComponentProps<TCardTransactionWithReferenceCode>> = ({
    onClose,
    modalData
}) => {
    const { t, i18n } = useTranslation('transaction_details');
    const { loyalty } = useUserContext();
    const [showFees, setShowFees] = useState(false);
    const [showReport, setShowReport] = useState(false);

    if (!modalData?.transaction) return null;

    const { transaction, merchant_name, transaction_type, transaction_id } = modalData;

    const internalAction = getInternalTransferAction(
        transaction.method,
        transaction.action,
        transaction.type
    );

    const isLoadBalance = internalAction === EFundsOperation.CARD_DEBIT_DEPOSIT;
    const isAtmWithdrawal =
        !isLoadBalance && (transaction_type?.toLowerCase().includes('atm') ?? false);

    const currencySymbol = currencies[transaction.currency as 'CAD']?.symbol ?? '$';
    const currencyCode = transaction.currency ?? 'CAD';
    const gross = transaction.amount + transaction.fee;
    const net = transaction.amount;

    const headerAmount = isLoadBalance
        ? `${currencySymbol}${prettifyAmount(net.toFixed(2))} ${currencyCode}`
        : isAtmWithdrawal
        ? `−${currencySymbol}${prettifyAmount(gross.toFixed(2))} ${currencyCode}`
        : `−${currencySymbol}${prettifyAmount(net.toFixed(2))} ${currencyCode}`;

    // Prefer the card network's own reference; fall back to our reference code alias
    // (never the raw internal transaction id).
    const txnId = transaction_id?.toString() ?? transaction.reference_code ?? '—';

    const statusLabel =
        ETransactionStatusTranslate[transaction.status]?.[i18n.language as 'en' | 'fr'] ?? '';

    const feePlan = internalAction
        ? getFeesPlanSendAndRequest({ loyalty, method: internalAction })
        : undefined;

    const feeRate = formatFeeRate({
        percent: feePlan?.percent,
        flat: feePlan?.flat,
        currency: transaction.currency ?? 'CAD'
    });
    const feeLabel = feeRate ? `XEPPT fee (${feeRate})` : 'XEPPT fee';

    const formattedFeeAmount = `${currencySymbol}${prettifyAmount(
        transaction.fee?.toFixed(2) ?? '0.00'
    )} ${currencyCode}`;

    const pillIcon = isLoadBalance ? 'arrow_down' : 'arrow_up';
    const pillLabel = isLoadBalance
        ? t('card_load_xeppt_card_balance')
        : isAtmWithdrawal
        ? t('card_atm_withdrawal')
        : t('card_payment_with_card');
    const avatarIcon = isLoadBalance
        ? 'outlined_wallet'
        : isAtmWithdrawal
        ? 'atm'
        : 'credit_card';

    if (showReport) {
        return (
            <div className={styles.wrapper}>
                <ReportProblemSubview
                    transactionRef={transaction.reference_code ?? ''}
                    onBack={() => setShowReport(false)}
                    onClose={onClose}
                />
            </div>
        );
    }

    if (showFees) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.fees_subview}>
                    <div className={styles.fees_subview_header}>
                        <button
                            type="button"
                            className={styles.fees_back_btn}
                            onClick={() => setShowFees(false)}>
                            <Icon name="arrow_left" width={20} height={20} />
                        </button>
                        <p className={styles.fees_subview_title}>Total fees</p>
                        <button
                            type="button"
                            className={styles.fees_close_btn}
                            onClick={onClose}>
                            <Icon name="close" width={20} height={20} />
                        </button>
                    </div>
                    <div className={styles.fees_rows}>
                        <div className={styles.fees_item}>
                            <p>{feeLabel}</p>
                            <p className={styles.row_value}>{formattedFeeAmount}</p>
                        </div>
                        <hr className={styles.fees_divider} />
                        <div className={styles.fees_item}>
                            <p>
                                <b>Total</b>
                            </p>
                            <p className={styles.row_value}>
                                <b>{formattedFeeAmount}</b>
                            </p>
                        </div>
                        <p className={styles.fees_subtext}>Included into the amount charged</p>
                    </div>
                    <InfoBlock variant="gray">
                        Fees are included in the amount charged from your wallet.
                    </InfoBlock>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.txn_detail_header}>
                <Typography fz={24} fw={700} alignment="center">
                    Transaction details
                </Typography>
                <button type="button" className={styles.txn_detail_close_btn} onClick={onClose}>
                    <Icon name="close" width={20} height={20} />
                </button>
            </div>
            <div className={styles.content}>
                <div className={styles.user}>
                    <div className={styles.user_avatar}>
                        <Icon name={avatarIcon} width={24} height={24} />
                    </div>
                    {merchant_name && <p className={styles.user_name}>{merchant_name}</p>}
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon name={pillIcon} />
                            {pillLabel}
                        </Typography>
                    </div>
                    <p className={styles.card_amount}>{headerAmount}</p>
                    <Status variant={getTransactionStatus(transaction.status)}>
                        {statusLabel}
                    </Status>
                </div>
                <div className={styles.details}>
                    <p className={styles.details_title}>{t('details')}</p>
                    <div className={styles.row}>
                        <p>{t('card_date_and_time')}</p>
                        <p>
                            <b>{formatDate(transaction.created_at, 'DD MMMM YYYY, HH:mm')}</b>
                        </p>
                    </div>
                    {isAtmWithdrawal && (
                        <>
                            <div className={styles.row}>
                                <p>{t('card_amount_you_withdrew')}</p>
                                <p>
                                    <b>
                                        {currencySymbol}
                                        {prettifyAmount(gross.toFixed(2))} {currencyCode}
                                    </b>
                                </p>
                            </div>
                            {transaction.fee > 0 && (
                                <div className={styles.row}>
                                    <p>
                                        {t('card_total_fees')}
                                        <button
                                            type="button"
                                            className={styles.fee_info_btn}
                                            onClick={() => setShowFees(true)}>
                                            <Icon name="info" width={16} height={16} />
                                        </button>
                                    </p>
                                    <p className={styles.row_value}>{formattedFeeAmount}</p>
                                </div>
                            )}
                            <div className={styles.row}>
                                <p>{t('card_amount_you_received')}</p>
                                <p>
                                    <b>
                                        {currencySymbol}
                                        {prettifyAmount(net.toFixed(2))} {currencyCode}
                                    </b>
                                </p>
                            </div>
                        </>
                    )}
                    {!isAtmWithdrawal && !isLoadBalance && (
                        <div className={styles.row}>
                            <p>{t('card_amount_you_paid')}</p>
                            <p>
                                <b>
                                    {currencySymbol}
                                    {prettifyAmount(gross.toFixed(2))} {currencyCode}
                                </b>
                            </p>
                        </div>
                    )}
                    {isLoadBalance && (
                        <>
                            <div className={styles.row}>
                                <p>{t('card_amount_charged_from_wallet')}</p>
                                <p>
                                    <b>
                                        {currencySymbol}
                                        {prettifyAmount(gross.toFixed(2))} {currencyCode}
                                    </b>
                                </p>
                            </div>
                            {transaction.fee > 0 && (
                                <div className={styles.row}>
                                    <p>
                                        {t('card_total_fees')}
                                        <button
                                            type="button"
                                            className={styles.fee_info_btn}
                                            onClick={() => setShowFees(true)}>
                                            <Icon name="info" width={16} height={16} />
                                        </button>
                                    </p>
                                    <p className={styles.row_value}>{formattedFeeAmount}</p>
                                </div>
                            )}
                            <div className={styles.row}>
                                <p>{t('card_amount_you_received')}</p>
                                <p>
                                    <b>
                                        {currencySymbol}
                                        {prettifyAmount(net.toFixed(2))} {currencyCode}
                                    </b>
                                </p>
                            </div>
                        </>
                    )}
                    {transaction.points > 0 && (
                        <div className={styles.row}>
                            <p>{t('card_points_you_earned')}</p>
                            <p>
                                <b>{transaction.points}</b>
                            </p>
                        </div>
                    )}
                    <div className={styles.row}>
                        <p>{t('card_transaction_id')}</p>
                        <p className={styles.row_value}>{txnId}</p>
                    </div>
                </div>
            </div>
            <div className={styles.actions_wrapper}>
                <div className={styles.actions}>
                    <ReportProblemButton onClick={() => setShowReport(true)} />
                </div>
            </div>
        </div>
    );
};

export default ViewCardTransactionModal;
